import {
  getBaseYearEndBounds,
  getBaseYearStartBounds,
  getBaseYearDateRange,
  getReportYearEndBounds,
  getReportYearStartBounds,
  getReportYearDateRange,
} from "../utils";

const formatDateDisplay = (value) => {
  if (!value) return "";
  const raw = String(value).slice(0, 10);
  const [year, month, day] = raw.split("-").map(Number);
  const date =
    year && month && day ? new Date(year, month - 1, day) : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const validateNxmapForm = (
  form = {},
  baseYearEndBounds,
  t,
  reportYearEndBounds,
  { step = 4 } = {},
) => {
  const message = (text) => (typeof t === "function" ? t(text) : text);
  const errors = {};

  const thresholdRaw = String(form.threshold ?? "").trim();
  const thresholdNum = Number(thresholdRaw);

  const shouldCheckStep = (targetStep) => Number(step) >= targetStep;

  if (shouldCheckStep(1)) {
    if (!form.organizationBoundary) {
      errors.organizationBoundary = message("Select organization boundary.");
    }
    if (!form.ghgReportPurpose) {
      errors.ghgReportPurpose = message("Select GHG reporting purpose.");
    }
    if (!thresholdRaw) {
      errors.threshold = message("Enter disclosure threshold (3-25).");
    } else if (Number.isNaN(thresholdNum)) {
      errors.threshold = message("Threshold must be a number.");
    } else if (thresholdNum < 3 || thresholdNum > 25) {
      errors.threshold = message("Threshold must be between 3 and 25.");
    }
    if (!form.reportingStandard) {
      errors.reportingStandard = message("Select reporting standard.");
    }
  }

  if (shouldCheckStep(2)) {
    if (!form.baseYear) {
      errors.baseYear = message("Select base year.");
    }
    if (!form.baseYearStart) {
      errors.baseYearStart = message("Select base year period start.");
    }
    if (!form.baseYearEnd) {
      errors.baseYearEnd = message("Select base year period end.");
    }
    if (!form.reportingYear) {
      errors.reportingYear = message("Select report year.");
    }
    if (!form.reportingPeriodStart) {
      errors.reportingPeriodStart = message("Select report year period start.");
    }
    if (!form.reportingPeriodEnd) {
      errors.reportingPeriodEnd = message("Select report year period end.");
    }
  }

  if (shouldCheckStep(2) && form.baseYear && form.baseYearStart) {
    const startBounds = getBaseYearStartBounds(form.baseYear);
    if (form.baseYearStart < startBounds.min) {
      errors.baseYearStart = message(
        "Start date cannot be earlier than January 1 of the base year.",
      );
    } else if (startBounds.max && form.baseYearStart > startBounds.max) {
      const { end } = getBaseYearDateRange(form.baseYear);
      errors.baseYearStart = message(
        `Start date must be earlier than ${formatDateDisplay(end)}.`,
      );
    }
  }

  const resolvedBounds =
    baseYearEndBounds ||
    getBaseYearEndBounds(form.baseYear, form.baseYearStart);

  if (
    shouldCheckStep(2) &&
    form.baseYearEnd &&
    resolvedBounds?.min &&
    form.baseYearEnd < resolvedBounds.min
  ) {
    errors.baseYearEnd = message(
      `End date should not be earlier than ${formatDateDisplay(resolvedBounds.min)}.`,
    );
  } else if (
    shouldCheckStep(2) &&
    form.baseYearEnd &&
    resolvedBounds?.max &&
    form.baseYearEnd > resolvedBounds.max
  ) {
    errors.baseYearEnd = message(
      `End date should not be later than ${formatDateDisplay(resolvedBounds.max)}.`,
    );
  }

  if (
    shouldCheckStep(2) &&
    form.baseYearStart &&
    form.baseYearEnd &&
    form.baseYearEnd <= form.baseYearStart
  ) {
    errors.baseYearEnd = message("End date must be later than start date.");
  }

  if (shouldCheckStep(2) && form.reportingYear && form.reportingPeriodStart) {
    const startBounds = getReportYearStartBounds(form.reportingYear);
    if (form.reportingPeriodStart < startBounds.min) {
      errors.reportingPeriodStart = message(
        "Start date cannot be earlier than January 1 of the report year.",
      );
    } else if (startBounds.max && form.reportingPeriodStart > startBounds.max) {
      const { end } = getReportYearDateRange(form.reportingYear);
      errors.reportingPeriodStart = message(
        `Start date must be earlier than ${formatDateDisplay(end)}.`,
      );
    }
  }

  const resolvedReportBounds =
    reportYearEndBounds ||
    getReportYearEndBounds(form.reportingYear, form.reportingPeriodStart);

  if (
    shouldCheckStep(2) &&
    form.reportingPeriodEnd &&
    resolvedReportBounds?.min &&
    form.reportingPeriodEnd < resolvedReportBounds.min
  ) {
    errors.reportingPeriodEnd = message(
      `End date should not be earlier than ${formatDateDisplay(resolvedReportBounds.min)}.`,
    );
  } else if (
    shouldCheckStep(2) &&
    form.reportingPeriodEnd &&
    resolvedReportBounds?.max &&
    form.reportingPeriodEnd > resolvedReportBounds.max
  ) {
    errors.reportingPeriodEnd = message(
      `End date should not be later than ${formatDateDisplay(resolvedReportBounds.max)}.`,
    );
  }

  if (
    shouldCheckStep(2) &&
    form.reportingPeriodStart &&
    form.reportingPeriodEnd &&
    form.reportingPeriodEnd <= form.reportingPeriodStart
  ) {
    errors.reportingPeriodEnd = message(
      "End date must be later than start date.",
    );
  }

  return errors;
};
